import { useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { RotateCw, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { applyUpdate } from "./apply-update";
import { scheduleUpdateChecks } from "./schedule-update-checks";
import { usePublishUpdateBannerVisible } from "./update-banner-visibility";

/**
 * Global, bottom-anchored bar that announces a freshly deployed version and
 * lets the user load it with one tap.
 *
 * It also owns service-worker update *detection*: the browser does not poll
 * for a new worker on its own, and iOS Safari never does for an installed PWA
 * resumed from its snapshot — so deployed updates would otherwise never reach
 * the device. See [[app-update-banner.spec]].
 */
export function AppUpdateBanner() {
  const [registration, setRegistration] =
    useState<ServiceWorkerRegistration | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    needRefresh: [needRefresh],
  } = useRegisterSW({
    onRegisteredSW(_swUrl, swRegistration) {
      if (swRegistration) setRegistration(swRegistration);
    },
  });

  // Detection: the browser will not poll for a new worker on its own, so the
  // app must — `scheduleUpdateChecks` does it on foreground, on an interval,
  // and on reconnect.
  useEffect(() => {
    if (!registration) return;
    return scheduleUpdateChecks(registration);
  }, [registration]);

  // A dismiss is a "later", not a "never": clear it whenever the app is
  // foregrounded so a still-waiting update reappears rather than being lost.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") setDismissed(false);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Tell the install banner whether this bar is on screen, so it can yield.
  const showing = needRefresh && !dismissed;
  usePublishUpdateBannerVisible(showing);

  if (!showing) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[60] flex items-center gap-3 border-t border-border bg-foreground px-4 pt-3 text-background animate-in fade-in slide-in-from-bottom-4 duration-200 motion-reduce:animate-none"
      style={{
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)",
      }}
    >
      <span className="min-w-0 flex-1 truncate text-sm">
        A new version of Otus is available.
      </span>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        loading={isRefreshing}
        onClick={() => {
          setIsRefreshing(true);
          // `registration` is set by the time the banner shows — `needRefresh`
          // only turns true after `onRegisteredSW` has run.
          applyUpdate(registration);
        }}
      >
        <RotateCw className="h-4 w-4" />
        Refresh
      </Button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        className="shrink-0 rounded-full p-1.5 text-background/70 transition-colors hover:bg-background/10 hover:text-background"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
